/**
 * PM-6 — TanStack Query hooks for the projects/v1 `project-import` status +
 * cancel surface. Mirrors the ai-findings `useImportJobStatus` /
 * `useCancelImportMutation` (750ms poll until terminal; idempotent cancel),
 * but talks to the projects domain via raw `fetch` + `getProjectsBaseUrl()`
 * (the `useImportAnalyze.ts` pattern) rather than the lyse `useApi()` axios
 * instance.
 *
 *  - `useImportStatusQuery(importId)` — GET /imports/{importId}; refetches
 *    every ~750ms while `running`, stops on a terminal status
 *    (`done | cancelled | failed`). `enabled`-gated on a non-null importId.
 *  - `useImportCancelMutation()` — POST /imports/{importId}/cancel (202,
 *    idempotent; already-committed rows are kept — no rollback, PM-D19).
 *
 * SYNC: projects/v1@2.7.0 (project-import, getProjectImportStatus /
 * cancelProjectImport).
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { getProjectsBaseUrl, useAuthStore } from "@aeriosiq/auth-client";
import type { ImportFetchError } from "./useImportCommit.js";
import type { ImportJobStatus } from "./types.js";

/** Poll cadence for the commit job (mirrors ai-findings: ~750ms). */
export const IMPORT_STATUS_POLL_INTERVAL_MS = 750;

const enc = encodeURIComponent;

function authHeaders(): Headers {
  const session = useAuthStore.getState().session;
  const tenant = useAuthStore.getState().tenant;
  const headers = new Headers();
  if (session?.accessToken) {
    headers.set("Authorization", `Bearer ${session.accessToken}`);
  }
  if (tenant?.tenantSlug) {
    headers.set("X-Tenant-ID", tenant.tenantSlug);
  }
  return headers;
}

async function readError(
  path: string,
  res: Response,
): Promise<ImportFetchError> {
  let parsedBody: unknown = null;
  try {
    parsedBody = await res.json();
  } catch {
    // non-JSON error body — status still surfaces
  }
  const err = new Error(
    `import status failed: ${path} → ${String(res.status)}`,
  ) as ImportFetchError;
  err.status = res.status;
  err.body = parsedBody;
  return err;
}

/**
 * GET /imports/{importId} — polls every ~750ms while the job is running,
 * stops once terminal. `enabled` holds off until an importId exists.
 */
export function useImportStatusQuery(importId: string | null) {
  return useQuery<ImportJobStatus, ImportFetchError>({
    queryKey: ["project-import", "job", importId],
    enabled: importId !== null,
    queryFn: async () => {
      const path = `/imports/${enc(importId ?? "")}`;
      const res = await fetch(`${getProjectsBaseUrl()}${path}`, {
        method: "GET",
        headers: authHeaders(),
      });
      if (!res.ok) throw await readError(path, res);
      return (await res.json()) as ImportJobStatus;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === undefined || status === "running") {
        return IMPORT_STATUS_POLL_INTERVAL_MS;
      }
      return false; // terminal — stop polling
    },
    refetchIntervalInBackground: false,
  });
}

/** POST /imports/{importId}/cancel — idempotent (202). */
export function useImportCancelMutation() {
  return useMutation<undefined, ImportFetchError, string>({
    mutationFn: async (importId) => {
      const path = `/imports/${enc(importId)}/cancel`;
      const res = await fetch(`${getProjectsBaseUrl()}${path}`, {
        method: "POST",
        headers: authHeaders(),
      });
      if (!res.ok) throw await readError(path, res);
      return undefined;
    },
  });
}
